import React, { JSX, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Box, Grid, Theme, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import PersonIcon from '@mui/icons-material/Person'
import EyeIcon from '@mui/icons-material/RemoveRedEye'
import GroupIcon from '@mui/icons-material/Group'
import ForumIcon from '@mui/icons-material/Forum'
import ReplyIcon from '@mui/icons-material/Reply'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import FavoriteIcon from '@mui/icons-material/Favorite'
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt'
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied'
import SaveIcon from '@mui/icons-material/Save'

import PopOverComponent from '@professocial/professocialFormComponents/FormComponents/PopOverComponent'
import TooltipComponent from '@professocial/professocialFormComponents/FormComponents/TooltipComponent'

import Clicks from '../../../../icons/Clicks'
import SurpriseIcon from '../../../../icons/Surprise'
import AngryIcon from '../../../../icons/Angry'

import { IPostElementProps } from '../../../../types/types'

import { getTheme } from '../../../../../store/common/common.selectors'

import { FacebookPostStatistics, InstagramPostStatistic, NetworkType } from '@3m5/sm_publish-frontend-api'
import Heart from '../../../../icons/Instagram/Heart'
import { ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import { LinkedInPostStatistics } from '@3m5/sm_publish-frontend-api'
import { addNumberDotSeparator } from '../../../../../util/addNumberDotSeparator'
import EngagementRate from '../../../../icons/EngagementRate'

interface StatisticItem {
  key: string
  title: string
  icon: ReactElement<any, any>
  count: string | number
}

interface PopOverData {
  icon: ReactElement<any, any>
  reaction: number
}

interface StatisticProps extends IPostElementProps {
  justifyContent?: 'start' | 'center' | 'end' | 'space-between'
}

const Statistic: React.FC<StatisticProps> = (props: StatisticProps) => {
  const useStyles = makeStyles((theme: Theme) => ({
    root: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.body1.fontSize,
      justifyContent: props.justifyContent || 'space-between',
      textTransform: 'inherit',
      width: '100%',
      minWidth: '350px',
      color: 'var(--customMainColor)',
    },
    itemContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignContent: 'center',
      alignItems: 'center',
      gap: '4px',
      marginRight: props.view === ViewEnum.single ? '16px' : '0',
    },
    item: {
      verticalAlign: 'top',
      fill: theme.palette.info.main,
    },
    popoverFont: {
      fontSize: '14px',
    },
    popOverWrap: {
      display: 'flex',
      gap: theme.spacing(1),
      color: 'var(--customMainColor)',
    },
    popOverItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
  }))

  const classes = useStyles()
  const theme = useSelector(getTheme)
  const { t } = useTranslation()

  const facebookPostStatistics: FacebookPostStatistics | undefined = props.networkType === NetworkType.Facebook && props.value
  const instagramPostStatistics: InstagramPostStatistic | undefined = props.networkType === NetworkType.Instagram && props.value
  const linkedInPostStatistics: LinkedInPostStatistics | undefined = props.networkType === NetworkType.LinkedIn && props.value

  let likeCount = 0
  if (facebookPostStatistics) {
    likeCount =
      facebookPostStatistics.postReactionsLikeTotal +
      facebookPostStatistics.postReactionsLoveTotal +
      facebookPostStatistics.postReactionsWowTotal +
      facebookPostStatistics.postReactionsHahaTotal +
      facebookPostStatistics.postReactionsSorryTotal +
      facebookPostStatistics.postReactionsAngerTotal
  }

  const facebookStatisticsItems: StatisticItem[] = [
    {
      key: 'postViews',
      title: t('postCommon:views'),
      icon: <EyeIcon className={classes.item} width='23px'/>,
      count: props.value?.postViews ?? 0,
    },
    {
      // Meta discontinued postImpressionsUnique (exact unique reach) with no replacement since Graph API v20.
      // postImpressions (view count, can include repeat views) is the closest available metric and is
      // already fetched today without extra API calls, same substitution pattern as the IG reach fix in ES-985.
      key: 'postImpressions',
      title: t('postCommon:personCount'),
      icon: <PersonIcon className={classes.item} width='23px'/>,
      count: facebookPostStatistics ? facebookPostStatistics.postImpressions : 0,
    },
    {
      key: 'postClicks',
      title: t('postCommon:postClicks'),
      icon: <Clicks className={classes.item} width='23px'/>,
      count: facebookPostStatistics ? facebookPostStatistics.postClicks : 0,
    },
    {
      key: 'likeCount',
      title: t('postCommon:reactions'),
      icon: <ThumbUpIcon className={classes.item} width='23px'/>,
      count: likeCount || 0,
    },
    {
      key: 'commentCount',
      title: t('postCommon:comments'),
      icon: <ForumIcon className={classes.item} width='23px'/>,
      count: facebookPostStatistics ? facebookPostStatistics.commentCount : 0,
    },
    {
      key: 'shares',
      title: t('postCommon:shares'),
      icon: <ReplyIcon className={classes.item} width='23px'/>,
      count: facebookPostStatistics ? facebookPostStatistics.shares : 0,
    },
    {
      key: 'postReactionsLikeTotal',
      title: t('postCommon:reactions'),
      icon: <ThumbUpIcon className={classes.item} width='23px'/>,
      count: facebookPostStatistics ? facebookPostStatistics.postReactionsLikeTotal : 0,
    },
    {
      key: 'postEngagedUsers',
      title: t('postCommon:engagementRate'),
      icon: <EngagementRate className={classes.item} width='23px'/>,
      count: `${facebookPostStatistics ? facebookPostStatistics.postEngagedUsers?.toFixed(2) : 0}%`,
    },
    {
      key: 'postEngagedFan',
      title: t('postCommon:relativeCoverage'),
      icon: <GroupIcon className={classes.item} width='23px'/>,
      count: `${facebookPostStatistics ? facebookPostStatistics.postEngagedFan?.toFixed(2) : 0}%`,
    },
  ]

  const instagramStatisticsItems: StatisticItem[] = [
    {
      key: 'reach',
      // ES-985: IG eye shows reach. Meta deprecated `impressions` (always 0 on Graph API v20)
      // and `videoViews` is empty for images; reach is populated for image + video and is
      // consistent with the IG feed overview, where the eye is also bound to reach.
      title: t('postCommon:reach'),
      icon: <EyeIcon className={classes.item} width='23px'/>,
      count: instagramPostStatistics ? instagramPostStatistics.reach : 0,
    },
    {
      key: 'engagement',
      title: t('postCommon:engagementRate'),
      icon: <Clicks className={classes.item} width='23px'/>,
      count: instagramPostStatistics ? instagramPostStatistics.engagement : 0,
    },
    {
      key: 'saved',
      title: t('postCommon:saved'),
      icon: <SaveIcon className={classes.item} width='23px'/>,
      count: instagramPostStatistics ? instagramPostStatistics.saved : 0,
    },
    {
      key: 'commentCount',
      title: t('postCommon:comments'),
      icon: <ForumIcon className={classes.item} width='23px'/>,
      count: instagramPostStatistics ? instagramPostStatistics.commentCount : 0,
    },
    {
      key: 'likeCount',
      title: t('postCommon:likes'),
      icon: <Heart className={classes.item} width='23px'/>,
      count: instagramPostStatistics ? instagramPostStatistics.likeCount : 0,
    },
  ]

  const linkedInStatisticsItems: StatisticItem[] = [
    {
      key: 'uniqueImpressionsCount',
      title: t('postCommon:personCount'),
      icon: <PersonIcon className={classes.item} width='23px'/>,
      count: linkedInPostStatistics ? linkedInPostStatistics.uniqueImpressionsCount : 0,
    },
    {
      key: 'impressionCount',
      title: t('postCommon:impressions'),
      icon: <EyeIcon className={classes.item} width='23px'/>,
      count: linkedInPostStatistics ? linkedInPostStatistics.impressionCount : 0,
    },
    {
      key: 'engagement',
      title: t('postCommon:engagementRate'),
      icon: <Clicks className={classes.item} width='23px'/>,
      count: linkedInPostStatistics ? linkedInPostStatistics.engagement : 0,
    },
    {
      key: 'shareCount',
      title: t('postCommon:shares'),
      icon: <ReplyIcon className={classes.item} width='23px'/>,
      count: linkedInPostStatistics ? linkedInPostStatistics.shareCount : 0,
    },
    {
      key: 'commentCount',
      title: t('postCommon:comments'),
      icon: <ForumIcon className={classes.item} width='23px'/>,
      count: linkedInPostStatistics ? linkedInPostStatistics.commentCount : 0,
    },
    {
      key: 'likeCount',
      title: t('postCommon:likes'),
      icon: <Heart className={classes.item} width='23px'/>,
      count: linkedInPostStatistics ? linkedInPostStatistics.likeCount : 0,
    },
  ]

  const popOverData: PopOverData[] = [
    {
      icon: <ThumbUpIcon className={classes.popoverFont}/>,
      reaction: facebookPostStatistics ? facebookPostStatistics.postReactionsLikeTotal : 0,
    },
    {
      icon: <FavoriteIcon className={classes.popoverFont}/>,
      reaction: facebookPostStatistics ? facebookPostStatistics.postReactionsLoveTotal : 0,
    },
    {
      icon: <SentimentSatisfiedAltIcon className={classes.popoverFont}/>,
      reaction: facebookPostStatistics ? facebookPostStatistics.postReactionsHahaTotal : 0,
    },
    {
      icon: <SurpriseIcon className={classes.popoverFont} color={theme.palette.info.main}/>,
      reaction: facebookPostStatistics ? facebookPostStatistics.postReactionsWowTotal : 0,
    },
    {
      icon: <SentimentVeryDissatisfiedIcon className={classes.popoverFont}/>,
      reaction: facebookPostStatistics ? facebookPostStatistics.postReactionsSorryTotal : 0,
    },
    {
      icon: <AngryIcon className={classes.popoverFont} color={theme.palette.info.main} width='14px'/>,
      reaction: facebookPostStatistics ? facebookPostStatistics.postReactionsAngerTotal : 0,
    },
  ]

  const PopOverContent = (): JSX.Element => (
    <div className={classes.popOverWrap}>
      {popOverData && popOverData.map((item, index) => {
        return (
          <Box key={index} className={classes.popOverItem}>
            <Box sx={{ display: 'flex' }}>{item.icon}</Box>
            <Box sx={{ lineHeight: '1px' }}>{item.reaction}</Box>
          </Box>
        )
      })}
    </div>
  )

  const statisticsItems = () => {
    switch (props.networkType) {
    case NetworkType.Facebook:
      return facebookStatisticsItems
    case NetworkType.Instagram:
      return instagramStatisticsItems
    case NetworkType.LinkedIn:
      return linkedInStatisticsItems
    } 
  }

  return (
    <Grid container className={classes.root}>
      {props.view && props.view === 'single' ? (
        <>
          {statisticsItems()?.filter(item => props.value[item.key] !== undefined && props.value[item.key] !== null)
            .map((item, index) => (
              <React.Fragment key={index}>
                <div className={classes.itemContainer}>
                  <TooltipComponent
                    title={item.title}
                    child={<span>{item.icon}</span>}
                  />
                  <Typography style={{ lineHeight: '19px' }}>
                    {typeof item.count === 'number' ? addNumberDotSeparator(item.count) : item.count}
                  </Typography>
                </div>
              </React.Fragment>
            ))}
        </>
      ) : (
        <>
          {
            statisticsItems()?.map((item, index) => {
              const template =
                <div key={index} className={classes.itemContainer}>
                  <TooltipComponent
                    title={item.title}
                    child={<span>{item.icon}</span>}
                  />
                  <Typography style={{ lineHeight: '19px' }}>
                    {typeof item.count === 'number' ? addNumberDotSeparator(item.count) : item.count}
                  </Typography>
                </div>

              return (item.title === t('postCommon:reactions')) && item.key !== 'postReactionsLikeTotal'
                ? <PopOverComponent key={index} popOverChild={template} popOverContent={<PopOverContent/>}/>
                : (item.key !== 'postReactionsLikeTotal' && template)
            })
          }
        </>
      )}
    </Grid>
  )
}

export default Statistic
